import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * About a 150-word answer in 1.5 s: fast enough to read as "arriving", never
 * slow enough to wait on. Long answers are capped so nothing types for long.
 */
const MS_PER_UNIT = 10;
const MAX_MS = 2500;
const TICK_MS = 16;

/**
 * A markdown link, a bold run and inline code are revealed whole — half a
 * link would flash `[the invo` as raw text — and everything else word by word.
 */
const UNIT = /\[[^\]\n]*\]\([^)\s]*\)|\*\*[^*\n]+\*\*|`[^`\n]+`|\S+/g;

/** The offsets the reveal may stop at: the end of each unit. */
export function revealCuts(text: string): number[] {
  return Array.from(text.matchAll(UNIT), (m) => (m.index ?? 0) + m[0].length);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Types out `text` once, client-side. Used for a checked answer that arrives
 * whole: the server no longer streams answers it may still take back, so the
 * typing is presentation only — the full text is already here. Off when
 * `enabled` is false or the user prefers reduced motion; `finish` jumps to the
 * end (any press inside the answer calls it, so a link is never a moving target).
 */
export function useTypingReveal(
  text: string,
  enabled: boolean
): { shown: string; revealing: boolean; finish: () => void } {
  const [animate] = useState(() => enabled && !prefersReducedMotion());
  const [done, setDone] = useState(!animate);
  // The first word shows at once: the answer starts appearing the moment it lands.
  const [cut, setCut] = useState(1);
  const started = useRef<number | null>(null);
  const cuts = useMemo(() => revealCuts(text), [text]);

  useEffect(() => {
    if (done) return;
    if (started.current === null) started.current = Date.now();
    const total = Math.min(cuts.length * MS_PER_UNIT, MAX_MS);
    const tick = () => {
      const elapsed = Date.now() - (started.current ?? Date.now());
      if (total === 0 || elapsed >= total) {
        setDone(true);
        return;
      }
      setCut(Math.max(1, Math.ceil((elapsed / total) * cuts.length)));
    };
    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [done, cuts]);

  const finish = useCallback(() => setDone(true), []);
  if (done) return { shown: text, revealing: false, finish };
  const end = cut > 0 ? (cuts[Math.min(cut, cuts.length) - 1] ?? 0) : 0;
  return { shown: text.slice(0, end), revealing: true, finish };
}
